import React, { useState, useEffect } from 'react';
import { useStrategyStore } from '../store';
import { strategiesAPI } from '../services/api';
import StrategyForm from '../components/StrategyForm';
import StrategyList from '../components/StrategyList';
import './StrategyBuilder.css';

function StrategyBuilder() {
  const [tab, setTab] = useState('list'); // 'list' or 'create'
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [loading, setLoading] = useState(false);

  const strategies = useStrategyStore((state) => state.strategies);
  const setStrategies = useStrategyStore((state) => state.setStrategies);
  const addStrategy = useStrategyStore((state) => state.addStrategy);
  const updateStrategy = useStrategyStore((state) => state.updateStrategy);
  const deleteStrategy = useStrategyStore((state) => state.deleteStrategy);

  // Fetch strategies on mount
  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    try {
      setLoading(true);
      const response = await strategiesAPI.list();
      setStrategies(response.data);
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStrategy = async (formData) => {
    try {
      setLoading(true);
      const response = await strategiesAPI.create(formData);
      addStrategy(response.data);
      setTab('list');
      // Reset form
      setEditingStrategy(null);
    } catch (error) {
      console.error('Failed to create strategy:', error);
      alert('Failed to create strategy. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStrategy = async (formData) => {
    if (!editingStrategy) return;
    try {
      setLoading(true);
      const response = await strategiesAPI.update(editingStrategy.id, formData);
      updateStrategy(editingStrategy.id, response.data);
      setEditingStrategy(null);
      setTab('list');
    } catch (error) {
      console.error('Failed to update strategy:', error);
      alert('Failed to update strategy. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStrategy = async (id) => {
    try {
      setLoading(true);
      await strategiesAPI.delete(id);
      deleteStrategy(id);
    } catch (error) {
      console.error('Failed to delete strategy:', error);
      alert('Failed to delete strategy. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      setLoading(true);
      const response = await strategiesAPI.update(id, { is_active: isActive });
      updateStrategy(id, response.data);
    } catch (error) {
      console.error('Failed to toggle strategy:', error);
      alert('Failed to update strategy. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStrategy = (strategy) => {
    // Convert stored config format to form format if needed
    setEditingStrategy(strategy);
    setTab('create');
  };

  const handleCancelEdit = () => {
    setEditingStrategy(null);
    setTab('list');
  };

  return (
    <div className="strategy-builder">
      <div className="strategy-builder-header">
        <h2>Trading Strategies</h2>
        <p className="subtitle">Create, manage, and optimize your trading strategies</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${tab === 'list' ? 'active' : ''}`}
          onClick={() => {
            setTab('list');
            setEditingStrategy(null);
          }}
        >
          Strategy Library ({strategies.length})
        </button>
        <button
          className={`tab-button ${tab === 'create' ? 'active' : ''}`}
          onClick={() => setTab('create')}
        >
          {editingStrategy ? '✎ Edit Strategy' : '+ Create Strategy'}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {tab === 'list' ? (
          <div className="tab-pane active">
            <StrategyList
              strategies={strategies}
              onEdit={handleEditStrategy}
              onDelete={handleDeleteStrategy}
              onToggleActive={handleToggleActive}
              loading={loading}
            />
          </div>
        ) : (
          <div className="tab-pane active">
            <StrategyForm
              initialData={editingStrategy}
              onSubmit={editingStrategy ? handleUpdateStrategy : handleCreateStrategy}
              onCancel={handleCancelEdit}
              loading={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default StrategyBuilder;
